import { ApiError } from '../types/api';

const API_FIELD_ALIASES: Record<string, string> = {
  newPassword: 'password',
  new_password: 'password',
  confirmPassword: 'confirmPassword',
  confirm_password: 'confirmPassword',
  currentPassword: 'currentPassword',
  current_password: 'currentPassword',
};

/**
 * Extract field-level error messages from an API error response.
 */
export function getFieldErrorsFromApi(
  error: ApiError | Error | unknown,
  fieldMap: Record<string, string> = API_FIELD_ALIASES
): Record<string, string> {
  if (typeof error !== 'object' || error === null || !('errors' in error)) {
    return {};
  }

  const apiError = error as ApiError;
  if (!apiError.errors) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(apiError.errors)) {
    const mappedField = fieldMap[field] ?? field;
    result[mappedField] = messages.join(', ');
  }

  return result;
}

/**
 * Format API error message for display
 */
export function formatApiError(error: ApiError | Error | unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const apiError = error as ApiError;
    if (apiError.errors) {
      // Format validation errors
      const errorMessages = Object.entries(apiError.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('\n');
      return errorMessages || apiError.message;
    }
    return apiError.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: ApiError | Error | unknown): boolean {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const apiError = error as ApiError;
    return apiError.status === 0 || apiError.status === undefined;
  }
  return false;
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: ApiError | Error | unknown): boolean {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const apiError = error as ApiError;
    return apiError.status === 401 || apiError.status === 403;
  }
  return false;
}

